/**
 * The chart's track-list star — the ☆ the screener grid and the symbol search
 * already carry, beside the symbol this chart is showing.
 *
 * ## Why the chart needs one
 *
 * The chart is where a symbol is most often *looked at*, and until this the only
 * way to file the charted symbol was to find it again somewhere else — its row
 * in the screener, or the search dialog. The same star and the same popover, so
 * the gesture is learnt once and works in three places.
 *
 * ## Inside the symbol box, not beside it
 *
 * The toolbar row is counted, not eyeballed: the symbol is centred by a mirror
 * spacer, and a key added beside it pushes it off centre by half its width. So
 * the star rides inside the box's left padding instead, and `ChartToolbar` adds
 * the same room on the right — the text stays centred — and takes both back off
 * the width, so the box's outer size and with it the row do not move at all.
 * `chartStarOffered` is how the toolbar knows whether to make that room.
 *
 * **Against the text, not against the box edge.** The box is transparent at rest
 * and its label is centred, so a star at the edge floats a different distance
 * from every name — 35px from `AAPL - Apple Inc.`, none from a name that fills
 * the box. So the label is measured, in the input's own font, and the star sits
 * just before it; a label wider than the box leaves it in the padding reserved
 * for it. Re-measured when the name resolves and when web fonts land, the two
 * things that move the text after the first paint.
 *
 * ## A ratio is filed as itself
 *
 * `AAPL/SPY` gets a star like any symbol, and the list holds `AAPL/SPY` — the
 * owner's call (2026-09-21), reversing the first version, which hid the star on
 * a ratio. It is filed in the **canonical composite spelling**: upper case, no
 * spaces, exactly what `SymbolDialog`'s commit writes into `ChartGroup.symbol`,
 * and exactly what the screener's Add row stores for the same keystrokes. One
 * ratio, one spelling, one row in the list whichever surface filed it.
 *
 * ## Absent, not disabled
 *
 * - **No list server, or no track-list feature** for this brand or account: the
 *   same two answers that decide whether the screener draws its star column.
 * - **No symbol** — an empty box has nothing to file.
 *
 * ## The close it records
 *
 * The symbol index's price, which is the figure the search dialog's star records
 * for the same symbol — so the two surfaces cannot file one symbol at two
 * prices. The chart's own last bar is not reachable from here (it lives in
 * `ChartGroupView`'s refs), and threading it up for this would move a number per
 * realtime tick through `App`. No price in the index sends none: null is
 * unknown, never zero (`docs/features/tracklists.md`). **A ratio has no row in
 * the index, so it always sends none** — nothing in the client computes a
 * composite's value, and an invented one would be worse than null.
 */

import { useLayoutEffect, useRef, useState } from "react";
import { mintUlid } from "@qufolio/model";

import { trackListsEnabled } from "../api/tracklists";
import { available } from "../lib/features";
import { symbolIndex, useSymbolName } from "../lib/symbol-index";
import {
  colourToken,
  createList,
  defaultList,
  listsFor,
  removeFromAll,
  toggleSymbols,
  useTrackLists,
  type TrackList,
} from "../lib/track-lists";
import { NewListDialog } from "./NewListDialog";
import { TrackListMenu } from "./TrackListMenu";
import { useTokenResolver } from "./theme";

/** Room the star takes inside the symbol box's left padding, in px. */
export const STAR_ROOM = 16;

/** Whether this chart's symbol gets a star at all — a ratio's included. */
export function chartStarOffered(symbol: string): boolean {
  if (!trackListsEnabled() || !available("tracklists")) return false;
  return listSymbolOf(symbol) !== "";
}

/**
 * What the list stores for this chart: the symbol, upper-cased, with any spaces
 * taken out — which only a composite could carry, and which `SymbolDialog`'s
 * commit has already removed from any ratio it charted. So for a ratio this is
 * the canonical composite, and for a ticker it is the ticker as before.
 */
function listSymbolOf(symbol: string): string {
  return symbol.replace(/\s+/g, "").toUpperCase();
}

/** Between the star and the first letter of the label, in px. */
const STAR_GAP = 3;

/**
 * Where the star's left edge goes, relative to the box it shares with `input`:
 * just before the centred label, and never outside the room reserved for it.
 *
 * Measured with a throwaway span in the input's computed font rather than a
 * canvas — jsdom has no canvas, and a span answers 0 there, which lands the star
 * at the box edge rather than throwing.
 */
function starLeft(input: HTMLInputElement, inset = 0): number {
  // `inset` is the symbol lock's room (`ChartSymbolLock`), which sits between
  // the star and the label as the mock draws it: ☆ 🔒 DIA.
  const left = labelStart(input) - STAR_GAP - STAR_ROOM - inset;
  return Math.max(input.offsetLeft + 2, left);
}

/**
 * Where the box's centred label begins, in the box's own coordinates.
 *
 * Shared with `ChartSymbolLock`, which sits just before the label the same way
 * — two measures of one label would drift the first time either changed.
 */
export function labelStart(input: HTMLInputElement): number {
  const style = getComputedStyle(input);
  const probe = document.createElement("span");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.whiteSpace = "pre";
  probe.style.fontFamily = style.fontFamily;
  probe.style.fontSize = style.fontSize;
  probe.style.fontWeight = style.fontWeight;
  probe.style.fontStyle = style.fontStyle;
  probe.style.letterSpacing = style.letterSpacing;
  probe.textContent = input.value;
  document.body.append(probe);
  const text = probe.getBoundingClientRect().width;
  probe.remove();

  // `|| 0`: a `calc()` padding that is not resolved to px (jsdom) parses to NaN,
  // and a NaN here would put the star — and the lock — at `left: NaN`.
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  const room = input.clientWidth - padding;
  const shown = Math.min(text, Math.max(room, 0));
  return input.offsetLeft + (input.offsetWidth - shown) / 2;
}

/** The price the search dialog would record for this symbol, when it has one. */
function closeOf(symbol: string): Record<string, number> | undefined {
  const upper = symbol.toUpperCase();
  const price = symbolIndex()?.find((row) => row.symbol === upper)?.price;
  return typeof price === "number" && Number.isFinite(price) && price > 0
    ? { [upper]: price }
    : undefined;
}

export function ChartListStar({ symbol, inset = 0 }: { symbol: string; inset?: number }) {
  // Subscribed even while hidden, so a star fills the moment another surface
  // files this symbol — a filled star in the grid over a hollow one on the
  // chart is the disagreement the store exists to prevent.
  const { lists } = useTrackLists();
  const resolveToken = useTokenResolver();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const [left, setLeft] = useState(2);
  // The box's label is `symbolLabel(symbol, name)`, and the name arrives after
  // the first paint — subscribing to it is what re-runs the measure below.
  const name = useSymbolName(symbol);
  const offered = chartStarOffered(symbol);

  useLayoutEffect(() => {
    const input = button.current?.parentElement?.querySelector("input");
    if (!input) return;
    const place = () => setLeft(starLeft(input, inset));
    place();
    // A web font landing after first paint changes the label's width.
    let live = true;
    void document.fonts?.ready.then(() => live && place());
    return () => {
      live = false;
    };
  }, [symbol, name, offered, inset]);

  if (!offered) return null;

  const upper = listSymbolOf(symbol);
  const on = listsFor(upper);
  const colourOf = (list: TrackList) =>
    resolveToken(colourToken(list.colour)) ?? "var(--qf-accent)";

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={`Track lists for ${upper}`}
        aria-expanded={anchor !== null}
        // Names the lists when it is in some, which is the question a filled
        // star raises — "which ones?" — answered without opening anything.
        title={on.length ? `In ${on.map((l) => l.name).join(", ")}` : "Add to a list"}
        style={{
          ...STAR,
          left,
          // Hollow in the secondary text colour at full strength, not the muted
          // one at 65%: it was the search dialog's quiet star, and on a chart
          // the owner found it too faint to notice (2026-09-21).
          color: on.length ? colourOf(on[0]!) : "var(--qf-text-secondary)",
        }}
        onClick={(event) => {
          // Alt-click is the one-decision path the grid and the search dialog
          // both offer: straight into the default list, no popover.
          const target = defaultList();
          if (event.altKey && target) {
            void toggleSymbols(target.id, [upper], closeOf(upper)).catch(() => {});
            return;
          }
          setAnchor(anchor ? null : event.currentTarget.getBoundingClientRect());
        }}
      >
        {on.length ? "★" : "☆"}
      </button>

      {anchor && (
        <TrackListMenu
          anchor={anchor}
          symbols={[upper]}
          lists={lists}
          colourOf={colourOf}
          onToggle={(list) => {
            void toggleSymbols(list.id, [upper], closeOf(upper)).catch(() => {});
          }}
          onCreate={() => {
            setCreateError(null);
            setCreating(true);
            setAnchor(null);
          }}
          onRemoveAll={() => {
            void removeFromAll([upper]).catch(() => {});
            setAnchor(null);
          }}
          onClose={() => setAnchor(null)}
        />
      )}

      {creating && (
        <NewListDialog
          lists={lists}
          resolve={resolveToken}
          seedCount={1}
          error={createError}
          busy={createBusy}
          onCreate={(spec) => {
            setCreateBusy(true);
            setCreateError(null);
            void createList({
              id: mintUlid(),
              name: spec.name,
              hotKey: spec.hotKey,
              colour: spec.colour,
              isDefault: spec.isDefault,
              symbols: [upper],
              closes: closeOf(upper),
            })
              .then(() => setCreating(false))
              .catch((err: unknown) =>
                setCreateError(err instanceof Error ? err.message : String(err)),
              )
              .finally(() => setCreateBusy(false));
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

/**
 * Laid over the symbol box, just before its label, centred on its height; `left`
 * is measured at render.
 *
 * A real `<button>` rather than the search dialog's `span role=button`: here it
 * sits in a toolbar a keyboard can reach, and Tab should land on it.
 */
const STAR: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: STAR_ROOM,
  padding: 0,
  background: "transparent",
  border: "none",
  font: "inherit",
  fontSize: "1.05rem",
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
};
